import type { AppDispatch, RootState } from '../../store';
import { redoEquipment, undoEquipment } from '../equipment/equipmentSlice';
import { redo, undo } from './historySlice';

/**
 * Layout edits (tracks, junctions, moves) and equipment edits (tools, templates)
 * are recorded in two independent stacks: `state.history` and
 * `state.equipment.history`. Neither knows about the other, so "undo the last
 * thing I did" cannot be answered by either one alone.
 *
 * Both stacks timestamp their entries, so the merged view is just a matter of
 * comparing the two candidates:
 *
 *  - undo reverses whichever stack holds the *newest* not-yet-undone edit;
 *  - redo replays whichever stack holds the *oldest* undone edit, which exactly
 *    reverses the undo order because undo always peels edits off the end of the
 *    global timeline.
 *
 * Every UI affordance for undo/redo must go through here. Dispatching the
 * per-slice thunks directly is what made Ctrl+Z and the toolbar button disagree.
 */

/** Timestamp of the layout edit an undo would reverse, or null if there is none. */
const layoutUndoAt = (state: RootState): number | null =>
  state.history.past.at(-1)?.timestamp ?? null;

/** Timestamp of the equipment edit an undo would reverse, or null if there is none. */
const equipmentUndoAt = (state: RootState): number | null =>
  state.equipment.history.past.at(-1)?.timestamp ?? null;

/** Timestamp of the layout edit a redo would replay. `future[0]` is the oldest. */
const layoutRedoAt = (state: RootState): number | null =>
  state.history.future[0]?.timestamp ?? null;

/** Timestamp of the equipment edit a redo would replay. The last entry is the oldest. */
const equipmentRedoAt = (state: RootState): number | null =>
  state.equipment.history.future.at(-1)?.timestamp ?? null;

export const canUndoAny = (state: RootState): boolean =>
  layoutUndoAt(state) !== null || equipmentUndoAt(state) !== null;

export const canRedoAny = (state: RootState): boolean =>
  layoutRedoAt(state) !== null || equipmentRedoAt(state) !== null;

/** Reverse the most recent edit, whichever layer it belongs to. */
export const undoLast = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const layoutAt = layoutUndoAt(state);
  const equipmentAt = equipmentUndoAt(state);

  if (equipmentAt !== null && (layoutAt === null || equipmentAt > layoutAt)) {
    dispatch(undoEquipment());
    return;
  }
  if (layoutAt !== null) dispatch(undo());
};

/** Replay the most recently undone edit, whichever layer it belongs to. */
export const redoLast = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const layoutAt = layoutRedoAt(state);
  const equipmentAt = equipmentRedoAt(state);

  if (equipmentAt !== null && (layoutAt === null || equipmentAt < layoutAt)) {
    dispatch(redoEquipment());
    return;
  }
  if (layoutAt !== null) dispatch(redo());
};
